import React from 'react';
import { Button, Select, Input, Card, DatePicker, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { TFilterGroup, TFilterCondition, TSmartVariableDefinition } from '@/types/TSmartCRM';
import { FIELD_TYPE_CONFIGS, FILTER_OPERATOR_LABELS } from 'src/constants/smartCRM';
import { EFilterOperator, EFilterLogic, EFieldType } from 'src/enums/ESmartCRM';
import dayjs from 'dayjs';

const { Option } = Select;

interface EnrollmentFilterBuilderProps {
	filterGroups: TFilterGroup[];
	onChange: (groups: TFilterGroup[]) => void;
	fields: TSmartVariableDefinition[];
}

export const EnrollmentFilterBuilder: React.FC<EnrollmentFilterBuilderProps> = ({
	filterGroups = [],
	onChange,
	fields = [],
}) => {
	const addRuleGroup = () => {
		if (fields.length === 0) {
			return;
		}

		const newGroup: TFilterGroup = {
			id: `group_${Date.now()}`,
			name: `Enrollment Rule ${filterGroups.length + 1}`,
			logic: EFilterLogic.AND,
			conditions: [
				{
					id: `condition_${Date.now()}`,
					field: '',
					operator: EFilterOperator.EQUALS,
					value: null,
				},
			],
		};
		onChange([...filterGroups, newGroup]);
	};

	const removeRuleGroup = (groupId: string) => {
		onChange(filterGroups.filter((group) => group.id !== groupId));
	};

	const addCondition = (groupId: string) => {
		const newCondition: TFilterCondition = {
			id: `condition_${Date.now()}`,
			field: '',
			operator: EFilterOperator.EQUALS,
			value: null,
		};

		const updatedGroups = filterGroups.map((group) =>
			group.id === groupId ? { ...group, conditions: [...group.conditions, newCondition] } : group,
		);
		onChange(updatedGroups);
	};

	const removeCondition = (groupId: string, conditionId: string) => {
		const updatedGroups = filterGroups.map((group) =>
			group.id === groupId
				? { ...group, conditions: group.conditions.filter((c) => c.id !== conditionId) }
				: group,
		);
		onChange(updatedGroups);
	};

	const updateCondition = (
		groupId: string,
		conditionId: string,
		updates: Partial<TFilterCondition>,
	) => {
		const updatedGroups = filterGroups.map((group) =>
			group.id === groupId
				? {
						...group,
						conditions: group.conditions.map((condition) =>
							condition.id === conditionId
								? {
										...condition,
										...updates,
										...(updates.field && { operator: EFilterOperator.EQUALS, value: null }),
										...(updates.operator &&
											updates.operator !== condition.operator && { value: null }),
									}
								: condition,
						),
					}
				: group,
		);
		onChange(updatedGroups);
	};

	const getAvailableOperators = (fieldKey: string) => {
		const field = fields.find((f) => f.internalKey === fieldKey);
		if (!field) {
			return [];
		}

		const fieldType = field.fieldType;
		const config = FIELD_TYPE_CONFIGS[fieldType] || FIELD_TYPE_CONFIGS[fieldType as any];

		if (!config && fieldType) {
			const defaultOperators = [EFilterOperator.EQUALS, EFilterOperator.NOT_EQUALS];
			if (fieldType === 'Multiple Items') {
				return [EFilterOperator.CONTAINS, EFilterOperator.NOT_CONTAINS];
			}
			if (fieldType === 'Number') {
				return [
					EFilterOperator.EQUALS,
					EFilterOperator.NOT_EQUALS,
					EFilterOperator.GREATER_THAN,
					EFilterOperator.LESS_THAN,
				];
			}
			return defaultOperators;
		}

		return config ? config.operators : [];
	};

	const renderValueInput = (
		condition: TFilterCondition,
		groupId: string,
		selectedField?: TSmartVariableDefinition,
	) => {
		if (!selectedField || !condition.operator) {
			return <Input placeholder="Select field first" disabled size="large" className="w-full" />;
		}

		const fieldType = selectedField.fieldType;

		switch (fieldType) {
			case 'Yes/No':
			case EFieldType.BOOLEAN:
				return (
					<Select
						value={condition.value}
						placeholder="Select value"
						onChange={(value) => updateCondition(groupId, condition.id, { value })}
						className="w-full"
						size="large"
					>
						<Option value={true}>Yes</Option>
						<Option value={false}>No</Option>
					</Select>
				);

			case 'Pick From List':
			case EFieldType.SELECT:
				return (
					<Select
						value={condition.value}
						placeholder="Choose an option"
						onChange={(value) => updateCondition(groupId, condition.id, { value })}
						className="w-full"
						size="large"
						showSearch
						filterOption={(input, option: any) =>
							(option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
						}
					>
						{selectedField?.options?.map((option) => (
							<Option key={option} value={option}>
								{option}
							</Option>
						)) || []}
					</Select>
				);

			case 'Multiple Items':
			case EFieldType.MULTISELECT: {
				const multiValue = condition.value
					? typeof condition.value === 'string'
						? condition.value.split(',')
						: Array.isArray(condition.value)
							? condition.value
							: [condition.value]
					: [];

				return (
					<Select
						mode="multiple"
						value={multiValue.map(String)}
						placeholder="Choose options"
						onChange={(value) =>
							updateCondition(groupId, condition.id, { value: value as string[] })
						}
						className="w-full"
						size="large"
						showSearch
						filterOption={(input, option: any) =>
							(option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
						}
					>
						{selectedField?.options?.map((option) => (
							<Option key={option} value={option}>
								{option}
							</Option>
						)) || []}
					</Select>
				);
			}

			case 'Date':
			case EFieldType.DATE: {
				let dateValue = null;
				if (condition.value) {
					if (typeof condition.value === 'number' && !isNaN(condition.value)) {
						dateValue = dayjs(condition.value);
					} else if (typeof condition.value === 'string') {
						const numericValue = parseInt(condition.value);
						if (!isNaN(numericValue)) {
							dateValue = dayjs(numericValue);
						} else {
							const parsed = dayjs(condition.value);
							if (parsed.isValid()) {
								dateValue = parsed;
							}
						}
					} else if (condition.value instanceof Date && !isNaN(condition.value.getTime())) {
						dateValue = dayjs(condition.value);
					}
				}

				return (
					<DatePicker
						value={dateValue}
						placeholder="Select date"
						onChange={(date) => {
							updateCondition(groupId, condition.id, {
								value: date ? date.valueOf() : null,
							});
						}}
						className="w-full"
						size="large"
					/>
				);
			}

			case 'Number':
			case EFieldType.NUMBER:
				return (
					<Input
						type="number"
						value={condition.value ? String(condition.value) : ''}
						placeholder="Enter number"
						onChange={(e) =>
							updateCondition(groupId, condition.id, {
								value: e.target.value ? Number(e.target.value) : null,
							})
						}
						size="large"
						className="w-full"
					/>
				);

			case 'Text':
			case EFieldType.TEXT:
			default:
				return (
					<Input
						value={condition.value ? String(condition.value) : ''}
						placeholder="Enter value"
						onChange={(e) => updateCondition(groupId, condition.id, { value: e.target.value })}
						size="large"
						className="w-full"
					/>
				);
		}
	};

	const renderCondition = (
		groupId: string,
		condition: TFilterCondition,
		index: number,
		isLast: boolean,
	) => {
		if (!condition || !condition.id) {
			return null;
		}

		const selectedField = fields.find((f) => f.internalKey === condition.field);
		const availableOperators = condition.field ? getAvailableOperators(condition.field) : [];

		const allowedFdFields = ['fd_lead_created_at', 'fd_lead_source'];
		const filteredFields = fields.filter((field) => {
			if (!field.internalKey?.startsWith('fd_')) {
				return true;
			}
			return allowedFdFields.includes(field.internalKey);
		});

		return (
			<div key={condition.id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center space-x-2">
						<Tag color="blue">Condition {index + 1}</Tag>
						{!isLast && <span className="text-xs text-gray-500">AND</span>}
					</div>
					<Button
						type="text"
						icon={<DeleteOutlined />}
						onClick={() => removeCondition(groupId, condition.id)}
						size="small"
						className="text-red-500 hover:text-red-700 hover:bg-red-50"
					/>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div>
						<label className="block text-xs font-medium text-gray-600 mb-2">Field</label>
						<Select
							value={condition.field || undefined}
							placeholder="Choose a field"
							onChange={(value) => updateCondition(groupId, condition.id, { field: value })}
							className="w-full"
							size="large"
							showSearch
							filterOption={(input, option: any) =>
								(option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
							}
						>
							{filteredFields?.map((field) => (
								<Option key={field.internalKey} value={field.internalKey}>
									{field.fieldName}
								</Option>
							))}
						</Select>
					</div>

					<div>
						<label className="block text-xs font-medium text-gray-600 mb-2">Operator</label>
						<Select
							value={condition.operator || undefined}
							placeholder="Choose operator"
							onChange={(value) =>
								updateCondition(groupId, condition.id, { operator: value as EFilterOperator })
							}
							disabled={!condition.field}
							className="w-full"
							size="large"
						>
							{availableOperators.map((operator) => (
								<Option key={operator} value={operator}>
									{FILTER_OPERATOR_LABELS[operator]}
								</Option>
							))}
						</Select>
					</div>

					<div>
						<label className="block text-xs font-medium text-gray-600 mb-2">Value</label>
						{renderValueInput(condition, groupId, selectedField)}
					</div>
				</div>
			</div>
		);
	};

	if (filterGroups.length === 0) {
		return (
			<div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
				<UserAddOutlined className="text-4xl text-gray-400 mb-4" />
				<p className="text-gray-600 mb-6 text-lg">No enrollment rules defined</p>
				{fields.length === 0 ? (
					<div className="space-y-4">
						<Button type="primary" disabled icon={<PlusOutlined />} size="large" className="px-8">
							Add Your First Rule
						</Button>
					</div>
				) : (
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={addRuleGroup}
						size="large"
						className="px-8"
					>
						Add Your First Rule
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{filterGroups
				.filter((group) => group && group.id)
				.map((group, groupIndex) => (
					<Card
						key={group.id}
						className="border-2 border-gray-200 shadow-sm"
						title={
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-3">
									<Tag color="green" className="text-sm">
										Rule Group {groupIndex + 1}
									</Tag>
									<span className="text-sm text-gray-600">
										{(group.conditions || []).length} condition
										{(group.conditions || []).length !== 1 ? 's' : ''}
									</span>
								</div>
								<Button
									type="text"
									icon={<DeleteOutlined />}
									onClick={() => removeRuleGroup(group.id)}
									size="small"
									className="text-red-500 hover:text-red-700 hover:bg-red-50"
								>
									Remove Group
								</Button>
							</div>
						}
					>
						<div className="space-y-4">
							{(group.conditions || [])
								.filter((condition) => condition && condition.id)
								.map((condition, conditionIndex) =>
									renderCondition(
										group.id,
										condition,
										conditionIndex,
										conditionIndex === (group.conditions || []).length - 1,
									),
								)}

							<Button
								type="dashed"
								icon={<PlusOutlined />}
								onClick={() => addCondition(group.id)}
								className="w-full py-6 text-blue-600 border-blue-300 hover:border-blue-500"
								size="large"
							>
								Add Another Condition
							</Button>
						</div>
					</Card>
				))}

			<div className="flex justify-center pt-4">
				<Button
					type="primary"
					ghost
					icon={<PlusOutlined />}
					onClick={addRuleGroup}
					size="large"
					className="px-8"
					disabled={fields.length === 0}
				>
					Add Rule Group
				</Button>
			</div>
		</div>
	);
};
